import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { AuthState, AuthContextType, UserInfo, UserRole, MfaSetupResponse, MfaStatusResponse, LoginResponse } from '../types/Auth';
import authService from '../services/authService';

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: true,
  error: null,
};

// Action types
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: UserInfo; token: string; refreshToken: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: UserInfo }
  | { type: 'SET_TOKEN'; payload: string };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        loading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        loading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_TOKEN':
      return {
        ...state,
        token: action.payload,
      };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if running locally
  const isLocalDevelopment = useCallback(() => {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '' ||
      process.env.NODE_ENV === 'development'
    );
  }, []);

  // Check if login bypass is enabled for local development
  const shouldBypassLogin = useCallback(() => {
    const bypassEnabled = process.env.REACT_APP_BYPASS_LOGIN_LOCAL === 'true';
    return isLocalDevelopment() && bypassEnabled;
  }, [isLocalDevelopment]);

  // Initialize authentication state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // Auto-authenticate when running locally and bypass is enabled
        if (shouldBypassLogin()) {
          const mockUser: UserInfo = {
            id: 'local-user',
            email: 'local@localhost',
            name: 'Local User',
            role: 'admin' as UserRole,
            permissions: ['read', 'write', 'admin'],
          };
          const mockToken = 'local-dev-token';
          const mockRefreshToken = 'local-dev-refresh-token';
          
          dispatch({ type: 'LOGIN_SUCCESS', payload: { 
            user: mockUser, 
            token: mockToken, 
            refreshToken: mockRefreshToken 
          }});
          return;
        }

        if (authService.isAuthenticated()) {
          // Try to get current user info
          const user = await authService.getCurrentUser();
          dispatch({ type: 'SET_USER', payload: user });
          dispatch({ type: 'LOGIN_SUCCESS', payload: { 
            user, 
            token: authService.getToken() || '', 
            refreshToken: authService.getRefreshToken() || '' 
          }});
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        // Only logout if not bypassing login locally
        if (!shouldBypassLogin()) {
          dispatch({ type: 'LOGOUT' });
        } else {
          // Auto-authenticate on error in local development when bypass is enabled
          const mockUser: UserInfo = {
            id: 'local-user',
            email: 'local@localhost',
            name: 'Local User',
            role: 'admin' as UserRole,
            permissions: ['read', 'write', 'admin'],
          };
          dispatch({ type: 'LOGIN_SUCCESS', payload: { 
            user: mockUser, 
            token: 'local-dev-token', 
            refreshToken: 'local-dev-refresh-token' 
          }});
        }
      }
    };

    initializeAuth();
  }, [shouldBypassLogin]);

  // Context methods - memoized to prevent infinite re-renders
  const login = useCallback(async (email: string, password: string): Promise<{ mfaRequired?: boolean; mfaSetupRequired?: boolean; tempToken?: string }> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      // Read trust token from localStorage for "Remember this device" feature
      const trustToken = localStorage.getItem('mfaTrustToken') || undefined;
      const response = await authService.login(email, password, trustToken);
      
      console.log('AuthContext login response:', response);
      console.log('MFA Required value:', response.mfaRequired, 'Type:', typeof response.mfaRequired);
      console.log('MFA Setup Required value:', response.mfaSetupRequired, 'Type:', typeof response.mfaSetupRequired);
      
      // If MFA setup is required, return early without setting auth state
      const mfaSetupRequired = response.mfaSetupRequired === true || String(response.mfaSetupRequired) === "true";
      if (mfaSetupRequired) {
        console.log('MFA setup required in AuthContext, returning early');
        dispatch({ type: 'SET_LOADING', payload: false });
        return { mfaSetupRequired: true, mfaRequired: false };
      }
      
      // If MFA verification is required (MFA already set up), return early without setting auth state
      // Check explicitly for true (not just truthy) to handle false/undefined correctly
      const mfaRequired = response.mfaRequired === true || String(response.mfaRequired) === "true";
      if (mfaRequired) {
        console.log('MFA required in AuthContext, returning early');
        dispatch({ type: 'SET_LOADING', payload: false });
        return { mfaRequired: true, mfaSetupRequired: false, tempToken: response.tempToken };
      }
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { 
          user: response.user, 
          token: response.token || '', 
          refreshToken: response.refreshToken || '' 
        } 
      });
      
      authService.setStoredUser(response.user);
      return {};
    } catch (error: any) {
      console.error('AuthContext login error:', error);
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
      authService.clearStoredUser();
    }
  }, []);

  const refreshAuthToken = useCallback(async (): Promise<void> => {
    try {
      const newToken = await authService.refreshToken();
      dispatch({ type: 'SET_TOKEN', payload: newToken });
    } catch (error) {
      dispatch({ type: 'LOGOUT' });
      authService.clearStoredUser();
      throw error;
    }
  }, []);

  const changePassword = useCallback(async (newPassword: string): Promise<void> => {
    try {
      await authService.changePassword(newPassword);
    } catch (error: any) {
      throw error;
    }
  }, []);

  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // MFA methods
  const setupMfa = useCallback(async (email: string): Promise<MfaSetupResponse> => {
    try {
      return await authService.setupMfa(email);
    } catch (error: any) {
      throw error;
    }
  }, []);

  const verifyMfaSetup = useCallback(async (email: string, code: string): Promise<LoginResponse> => {
    try {
      const response = await authService.verifyMfaSetup(email, code);
      
      // Backend automatically logs in after MFA setup
      if (response.token && response.refreshToken) {
        dispatch({ 
          type: 'LOGIN_SUCCESS', 
          payload: { 
            user: response.user, 
            token: response.token || '', 
            refreshToken: response.refreshToken || '' 
          } 
        });
        authService.setStoredUser(response.user);
      }
      
      return response;
    } catch (error: any) {
      throw error;
    }
  }, []);

  const verifyMfaLogin = useCallback(async (email: string, code: string, tempToken: string, trustDevice: boolean = true): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.verifyMfaLogin(email, code, tempToken, trustDevice);
      
      dispatch({ 
        type: 'LOGIN_SUCCESS', 
        payload: { 
          user: response.user, 
          token: response.token || '', 
          refreshToken: response.refreshToken || '' 
        } 
      });
      
      authService.setStoredUser(response.user);
    } catch (error: any) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
      throw error;
    }
  }, []);

  const disableMfa = useCallback(async (code: string): Promise<void> => {
    try {
      await authService.disableMfa(code);
    } catch (error: any) {
      throw error;
    }
  }, []);

  const getMfaStatus = useCallback(async (): Promise<MfaStatusResponse> => {
    try {
      return await authService.getMfaStatus();
    } catch (error: any) {
      throw error;
    }
  }, []);

  // SSO entry point — called by /sso-callback after the on-prem portal
  // hands off HMAC-validated tokens. Stores the cookies the same way a
  // normal login does, then fetches /auth/me to populate user state. If
  // anything fails (bad token, /me call rejected), the cookies are cleared
  // so the user is left in a clean unauthenticated state and the caller
  // can redirect to /login.
  const establishSsoSession = useCallback(async (token: string, refreshToken: string): Promise<void> => {
    try {
      dispatch({ type: 'LOGIN_START' });
      authService.acceptSsoTokens(token, refreshToken);
      const user = await authService.getCurrentUser();
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token, refreshToken }
      });
      authService.setStoredUser(user);
    } catch (error: any) {
      console.error('SSO session establishment failed:', error);
      await authService.logout(); // clears cookies
      dispatch({ type: 'LOGIN_FAILURE', payload: 'SSO sign-in failed' });
      throw error;
    }
  }, []);

  const contextValue: AuthContextType = useMemo(() => ({
    ...state,
    login,
    logout,
    refreshAuthToken,
    changePassword,
    clearError,
    establishSsoSession,
    setupMfa,
    verifyMfaSetup,
    verifyMfaLogin,
    disableMfa,
    getMfaStatus,
  }), [state, login, logout, refreshAuthToken, changePassword, clearError, establishSsoSession, setupMfa, verifyMfaSetup, verifyMfaLogin, disableMfa, getMfaStatus]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
